import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * Ancestor the `variant:update` event bubbles through. Mirrors shipping-status.js.
 * @constant {string}
 */
const VARIANT_EVENT_TARGET = '[id*="ProductInformation-"], [id*="QuickAdd-"], product-card';

/** Klaviyo's client-side endpoint. Public key only, sent as a query parameter. */
const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';

/** API version the request body below is written against. */
const KLAVIYO_REVISION = '2026-07-15';

/** Klaviyo's catalog id for a Shopify variant. */
const SHOPIFY_VARIANT_PREFIX = '$shopify:::$default:::';

/** Token the Liquid template leaves in place of the variant title. */
const VARIANT_PLACEHOLDER = '__VARIANT__';

/**
 * @typedef {object} BackInStockRefs
 * @property {HTMLButtonElement} [trigger] - Button that reveals the form.
 * @property {HTMLElement} [panel] - Collapsible wrapper around the form, inline mode only.
 * @property {HTMLDialogElement} [dialog] - The modal, modal mode only.
 * @property {HTMLFormElement} [form] - The subscription form.
 * @property {HTMLInputElement} [email] - Email field.
 * @property {HTMLInputElement} [consent] - Consent checkbox, when enabled.
 * @property {HTMLButtonElement} [submit] - Submit button.
 * @property {HTMLElement} [message] - Live region for validation and result messages.
 * @property {HTMLElement} [announcement] - Live region announcing the trigger.
 */

/**
 * Collects a back in stock subscription for the selected variant and posts it to Klaviyo.
 *
 * The initial visibility is resolved in Liquid; this component only flips the `hidden`
 * attribute when the shopper changes variant, and owns the form interaction.
 *
 * It talks to Klaviyo directly instead of relying on the onsite bundle, so it works
 * whether or not a Back in Stock form has been published in the Klaviyo admin.
 *
 * Extends DialogComponent so the modal display mode reuses the theme's dialog behaviour
 * (scroll lock, backdrop click, Escape, close animation) instead of reimplementing it.
 *
 * @extends {DialogComponent<BackInStockRefs>}
 */
class BackInStock extends DialogComponent {
  // DialogComponent requires a `dialog` ref, but the inline mode has no dialog at all.
  // The dialog methods are only ever called when `display-mode` is `modal`.
  requiredRefs = [];

  #abortController = new AbortController();

  /** @returns {boolean} Whether the form lives in a modal. */
  get isModal() {
    return this.dataset.displayMode === 'modal';
  }

  connectedCallback() {
    super.connectedCallback();

    this.closest(VARIANT_EVENT_TARGET)?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** Reveals the form, in whichever way this block is configured to show it. */
  openForm() {
    if (this.isModal) {
      this.showDialog();
      // showDialog defers the actual open to the next frame, so focus has to wait too.
      requestAnimationFrame(() => this.#focusEmail());
      return;
    }

    const { panel, trigger } = this.refs;

    if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

    const willOpen = panel.hasAttribute('hidden');

    panel.toggleAttribute('hidden', !willOpen);
    trigger.setAttribute('aria-expanded', String(willOpen));

    if (willOpen) this.#focusEmail();
  }

  /** Moves focus to the email field so keyboard users land straight in the form. */
  #focusEmail() {
    const { email } = this.refs;

    if (email instanceof HTMLInputElement) email.focus();
  }

  /**
   * Validates locally, then posts the subscription.
   *
   * @param {SubmitEvent} event - The form submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { email, consent, submit } = this.refs;

    if (!(email instanceof HTMLInputElement)) return;

    // `novalidate` is set on the form so the messages land in our own live region
    // instead of a native bubble that screen readers announce inconsistently.
    if (!email.value || !email.checkValidity()) {
      email.setAttribute('aria-invalid', 'true');
      this.#setMessage(this.dataset.messageInvalidEmail, 'error');
      email.focus();
      return;
    }

    email.removeAttribute('aria-invalid');

    if (this.dataset.requireConsent === 'true' && consent instanceof HTMLInputElement && !consent.checked) {
      this.#setMessage(this.dataset.messageConsent, 'error');
      consent.focus();
      return;
    }

    if (submit instanceof HTMLButtonElement) submit.setAttribute('aria-busy', 'true');
    this.#setMessage('', null);

    try {
      const response = await this.#subscribe(email.value);

      if (response.ok) {
        this.classList.add('back-in-stock--subscribed');
        this.#setMessage(this.dataset.messageSuccess, 'success');
        return;
      }

      this.#setMessage(await this.#errorMessage(response), 'error');
    } catch (error) {
      // Network failure, blocked request, or Klaviyo unreachable.
      console.error('Back in stock subscription failed', error);
      this.#setMessage(this.dataset.messageError, 'error');
    } finally {
      if (submit instanceof HTMLButtonElement) submit.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} emailAddress - The address to subscribe.
   * @returns {Promise<Response>} The raw Klaviyo response; 202 means accepted.
   */
  #subscribe(emailAddress) {
    const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId ?? '')}`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email: emailAddress },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `${SHOPIFY_VARIANT_PREFIX}${this.dataset.variantId}`,
              },
            },
          },
        },
      }),
      signal: this.#abortController.signal,
    });
  }

  /**
   * Klaviyo answers with a JSON:API error document. Its `detail` strings are English and
   * developer facing, so they are logged rather than shown to the shopper.
   *
   * @param {Response} response - The failed response.
   * @returns {Promise<string | undefined>} The message to display.
   */
  async #errorMessage(response) {
    try {
      const body = await response.json();
      const detail = body?.errors?.[0]?.detail;

      if (detail) console.error('Back in stock subscription rejected by Klaviyo:', detail);
    } catch {
      // Body was not JSON. The generic message below still applies.
    }

    return this.dataset.messageError;
  }

  /**
   * @param {string | undefined} text - Message to announce, empty to clear.
   * @param {'error' | 'success' | null} state - Styling hook.
   */
  #setMessage(text, state) {
    const { message } = this.refs;

    if (!(message instanceof HTMLElement)) return;

    message.textContent = text ?? '';
    message.toggleAttribute('hidden', !text);

    if (state) {
      message.dataset.state = state;
    } else {
      delete message.dataset.state;
    }
  }

  /**
   * Reacts to a variant change.
   *
   * @param {Event} event - The `variant:update` event dispatched by `<variant-picker>`.
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;

    if (!detail) return;

    const { resource, data } = detail;

    // Combined listings swap the product entirely.
    if (data?.newProduct) {
      this.dataset.productId = String(data.newProduct.id);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // `resource` is null when the option combination does not exist: there is no variant
    // to subscribe to, so the form stays hidden.
    const canNotify = resource != null && resource.available === false;
    const wasHidden = this.hasAttribute('hidden');

    this.toggleAttribute('hidden', !canNotify);
    this.removeAttribute('data-editor-preview');

    if (resource?.id) {
      this.dataset.variantId = String(resource.id);
      this.#updateTriggerLabel(resource.title);
    }

    // A subscription belongs to one variant, so switching variants starts over.
    this.#reset();

    if (canNotify && wasHidden) this.#announce();
  };

  /**
   * Liquid rendered the label for the variant selected on load, so it has to be rebuilt
   * here or the trigger keeps announcing the previous variant.
   *
   * @param {string | undefined} variantTitle - Title of the newly selected variant.
   */
  #updateTriggerLabel(variantTitle) {
    const { trigger } = this.refs;
    const template = this.dataset.variantLabelTemplate;

    if (!(trigger instanceof HTMLElement) || !template || !variantTitle) return;

    trigger.setAttribute('aria-label', template.replace(VARIANT_PLACEHOLDER, variantTitle));
  }

  /** Returns the block to its collapsed, unsubmitted state. */
  #reset() {
    const { panel, trigger, form, email, dialog } = this.refs;

    this.classList.remove('back-in-stock--subscribed');
    this.#setMessage('', null);

    if (form instanceof HTMLFormElement) form.reset();
    if (email instanceof HTMLInputElement) email.removeAttribute('aria-invalid');

    if (this.isModal) {
      // Only close what is open: closeDialog restores body scroll, and calling it on a
      // closed dialog would be a no-op anyway, but this keeps the intent explicit.
      if (dialog instanceof HTMLDialogElement && dialog.open) this.closeDialog();
      return;
    }

    if (panel instanceof HTMLElement) panel.setAttribute('hidden', '');
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'false');
  }

  /** Announces the trigger to assistive technology once it becomes available. */
  #announce() {
    const { announcement, trigger } = this.refs;

    if (!(announcement instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

    announcement.textContent = trigger.getAttribute('aria-label') ?? trigger.textContent;
  }
}

if (!customElements.get('back-in-stock')) {
  customElements.define('back-in-stock', BackInStock);
}
